import React from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  EntityRootData,
  EntityStatus,
  EntityStatusValue,
} from '@web-config-app/core';
import { diff } from 'just-diff';

import { UseEntityOperationsResult } from '@web-config-app/core-react';

interface EntityHeaderSaveStatusProps {
  operations?: UseEntityOperationsResult;
  status?: EntityStatus;
  entityRootData?: EntityRootData;
}

export type UseEntityHeaderSaveStatusResult = ReturnType<
  typeof useEntityHeaderSaveStatus
>;

export const useEntityHeaderSaveStatus = ({
  status,
  operations,
  entityRootData,
}: EntityHeaderSaveStatusProps) => {
  const { formatMessage } = useIntl();

  return React.useMemo(() => {
    if (!operations?.get?.data) {
      // is a new entity, changes are unsaved
      return {
        message: formatMessage({ id: 'UNSAVED_CHANGES' }),
        isEntitySaved: false,
      };
    }

    const diffResult = diff(operations?.get?.data, entityRootData ?? {});

    // basically, if the diff is empty OR is only a collection of added (not changed) empty arrays then
    // we consider the entity as saved
    const changesSaved =
      diffResult.length === 0 ||
      diffResult.filter(({ op, value }) => {
        const isAddedEmptyArray =
          op === 'add' &&
          ((typeof value === 'object' &&
            value !== null &&
            Object.values(value).filter(
              (changeObjectValue) =>
                !(
                  Array.isArray(changeObjectValue) &&
                  changeObjectValue.length === 0
                ),
            ).length === 0) ||
            (Array.isArray(value) && value.length === 0));
        return !isAddedEmptyArray;
      }).length === 0;

    const isDraft = status === EntityStatusValue.Draft;

    let messageKey;

    if (changesSaved) {
      messageKey = isDraft ? 'ALL_CHANGES_SAVED' : 'ALL_CHANGES_PUBLISHED';
    } else {
      messageKey = isDraft ? 'UNSAVED_CHANGES' : 'UNPUBLISHED_CHANGES';
    }

    return {
      message: formatMessage({ id: messageKey }),
      isEntitySaved: changesSaved,
    };
  }, [entityRootData, operations?.get?.data, status, formatMessage]);
};
